import { createFileRoute, Link } from '@tanstack/react-router';

import type { App } from '@keboola/api-client/developerPortal/types';
import {
  Alert,
  AlertModal,
  Breadcrumb,
  Button,
  Card,
  Markdown,
  PageHeader,
  PageLayout,
  Skeleton,
  toast,
} from '@keboola/design';
import { ExternalLink } from '@keboola/design/icons';

import { useAuth, useCurrentVendor } from '../../../../auth/context';
import { QueryError } from '../../../../components/QueryError';
import { AppIcon } from '../../../../features/apps/components/AppIcon';
import { ComponentBadges } from '../../../../features/apps/components/ComponentBadges';
import { PublicFlags } from '../../../../features/apps/components/PublicFlags';
import { getAppTitle } from '../../../../features/apps/helpers';
import {
  createKbcComponentUrl,
  hasAllFieldsForPublishing,
  hasRepository,
} from '../../../../features/apps/vendorHelpers';
import { getApiErrorMessage } from '../../../../features/auth/errors';
import {
  useRequestPublishing,
  useVendor,
  useVendorApp,
} from '../../../../features/vendors/queries';

export const Route = createFileRoute('/~/components/$appId/')({
  component: VendorComponentsAppidPage,
});

/** The vendor-only facts, shown below the public ones. */
function VendorFlags({ app }: { app: App }) {
  const uiOptions = app.uiOptions ?? [];
  const actions = app.actions ?? [];

  return (
    <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0 tw:text-sm tw:text-muted-foreground">
      <li>Default bucket: {app.defaultBucket === true ? 'Enabled' : 'Disabled'}</li>
      {app.defaultBucket === true && (
        // Nullable on the wire even with the bucket enabled, and React renders `null` as nothing — so
        // the label would read "Default bucket stage:" followed by a blank. Same fallback as the two
        // fields below.
        <li>Default bucket stage: {app.defaultBucketStage ?? 'default'}</li>
      )}
      {uiOptions.length > 0 && <li>UI options: {uiOptions.join(', ')}</li>}
      {actions.length > 0 && <li>Actions: {actions.join(', ')}</li>}
      <li>Forwards token: {app.forwardToken === true ? 'Yes' : 'No'}</li>
      <li>Forwards token details: {app.forwardTokenDetails === true ? 'Yes' : 'No'}</li>
      {/* Both fall back to "default" rather than being hidden: a vendor cannot edit these, so seeing
          that a default applies is the useful information. */}
      <li>Required memory: {app.requiredMemory ?? 'default'}</li>
      <li>
        Process timeout:{' '}
        {app.processTimeout === null || app.processTimeout === undefined
          ? 'default'
          : `${app.processTimeout}s`}
      </li>
    </ul>
  );
}

function VendorComponentsAppidPage() {
  const { appId } = Route.useParams();
  const vendorId = useCurrentVendor();
  const { identity } = useAuth();
  const { data: app, isLoading, isError, error, refetch } = useVendorApp(vendorId, appId);
  // `GET /vendors/{vendor}` rather than the whole list: the only field this page reads is `projectUrl`.
  const { data: vendor } = useVendor(vendorId);
  const requestPublishing = useRequestPublishing(vendorId);

  const title = app === undefined ? appId : getAppTitle(app);
  const projectUrl =
    app === undefined ? null : createKbcComponentUrl(vendor?.projectUrl, app.type, app.id);

  // The button hides once publishing is moot — public, deprecated, or already requested. There is no
  // disabled state: the original removes it entirely.
  const canRequestPublishing =
    app !== undefined &&
    app.isPublic !== true &&
    app.isDeprecated !== true &&
    app.publishingStatus !== 'requested';

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Detail
          breadcrumbs={
            <Breadcrumb>
              <Breadcrumb.List>
                <Breadcrumb.Item>
                  <Breadcrumb.Link asChild>
                    <Link to="/~">Components</Link>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator />
                <Breadcrumb.Item>
                  <Breadcrumb.Page>{title}</Breadcrumb.Page>
                </Breadcrumb.Item>
              </Breadcrumb.List>
            </Breadcrumb>
          }
          titleIcon={app === undefined ? undefined : <AppIcon app={app} size={48} />}
          title={title}
          badges={app === undefined ? undefined : <ComponentBadges app={app} />}
          description={app?.shortDescription}
          primaryAction={
            <Button asChild variant="primary">
              <Link to="/~/components/$appId/edit" params={{ appId }}>
                Edit
              </Link>
            </Button>
          }
          secondaryAction={
            canRequestPublishing ? (
              // The `try` is not optional: `AlertModal` settles the handler with `.finally()` and
              // closes on rejection too, so an uncaught failure would look exactly like a filed
              // request.
              <AlertModal
                onConfirm={async () => {
                  try {
                    await requestPublishing.mutateAsync(appId);
                    toast.success('Publishing requested. A Keboola administrator will review it.');
                  } catch (error) {
                    toast.error(getApiErrorMessage(error, 'Could not request publishing.'));
                  }
                }}
              >
                <AlertModal.Trigger asChild>
                  <Button
                    variant="secondary"
                    // All-or-nothing: the original only offers this once every publishing field is
                    // filled in, so a half-finished component never reaches the review queue.
                    disabled={!hasAllFieldsForPublishing(app)}
                  >
                    Request publishing
                  </Button>
                </AlertModal.Trigger>
                <AlertModal.Content
                  variant="info"
                  title="Request publishing?"
                  confirmLabel="Request publishing"
                >
                  A Keboola administrator will review this component. Once approved it becomes
                  visible to every Keboola user.
                </AlertModal.Content>
              </AlertModal>
            ) : undefined
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      {/* Blocking only with nothing to show: a failed refetch keeps the previously loaded component in
          `data`, and taking a readable detail page away mid-edit is worse than a banner over it.
          `as="h2"`: this sits directly under the page header's `h1`. */}
      <QueryError
        isError={isError}
        hasData={app !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load this component"
        refreshTitle="Could not refresh this component"
        staleNote="Showing it as of the last successful load."
        notFound={{
          title: 'This component does not exist',
          description:
            'It may have been removed, or the link may be out of date. Browse the catalog to find what you are looking for.',
        }}
        titleAs="h2"
      />

      {app !== undefined && (
        <div className="tw:flex tw:flex-col tw:gap-6">
          {!hasRepository(app) && (
            <Alert variant="warning" title="This component is not deployed">
              It cannot be used in Keboola until a repository is set up.{' '}
              <a
                href="https://developers.keboola.com/extend/component/deployment/"
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-primary"
              >
                Read about component deployment
              </a>
              .
            </Alert>
          )}

          {app.publishingStatus === 'rejected' &&
            typeof app.publishingRequest?.rejectionReason === 'string' && (
              <Alert variant="error" title="Publishing was rejected">
                {app.publishingRequest.rejectionReason}
              </Alert>
            )}

          {canRequestPublishing && !hasAllFieldsForPublishing(app) && (
            <Alert variant="info" title="Not ready to publish yet">
              Publishing needs an icon, a repository, a short and long description, a license URL
              and a documentation URL.
            </Alert>
          )}

          <div className="tw:grid tw:grid-cols-1 tw:gap-6 tw:lg:grid-cols-3">
            <Card className="tw:lg:col-span-2">
              <Card.Content className="tw:flex tw:flex-col tw:gap-4">
                <PublicFlags app={app} />
                <hr className="tw:m-0 tw:border-border" />
                <VendorFlags app={app} />
              </Card.Content>
            </Card>

            <Card>
              <Card.Content className="tw:flex tw:flex-col tw:gap-3">
                <div>
                  <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Developer</p>
                  <p className="tw:m-0 tw:font-semibold tw:text-foreground">
                    {app.vendor.name ?? app.vendor.id}
                  </p>
                </div>
                {identity?.email !== null && identity?.email !== undefined && (
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    Signed in as {identity.email}
                  </p>
                )}
                {/* Only appears when the vendor has a usable `projectUrl` AND the type has a component
                    page in a project — see `createKbcComponentUrl`. */}
                {projectUrl !== null && (
                  <Button asChild variant="secondary">
                    <a href={projectUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink />
                      Open in a test project
                    </a>
                  </Button>
                )}
              </Card.Content>
            </Card>
          </div>

          {typeof app.longDescription === 'string' && app.longDescription !== '' && (
            <div className="tw:w-full tw:max-w-3xl">
              <Markdown rhythm="chrome">{app.longDescription}</Markdown>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
}
